// components/analytics/components/charts/ActivityTrendChart.tsx
import { Line } from '@antv/g2plot';
import { useEffect, useRef } from 'react';
import { Skeleton } from 'antd';
import NoDataPlaceholder from '../NoDataPlaceholder';

type ActivityPeriod = 'hourly' | 'daily' | 'weekly' | 'monthly';

interface ChartDataItem {
  timeLabel: string | number;
  value: number;
  type: string;
}

interface ActivityTrendChartProps {
  hourlyData?: Array<{hour: number; count: number; type: string}>;
  dailyData?: Array<{day: string; count: number; type: string}>;
  weeklyData?: Array<{week: number; count: number; type: string}>;
  monthlyData?: Array<{month: string; count: number; type: string}>;
  period: ActivityPeriod;
  height?: number;
  loading?: boolean;
}

export const ActivityTrendChart: React.FC<ActivityTrendChartProps> = ({
  hourlyData = [], 
  dailyData = [], 
  weeklyData = [], 
  monthlyData = [],
  period = 'hourly',
  height = 300,
  loading = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Line | null>(null);

  // Transform data based on period
  const getChartData = (): ChartDataItem[] => {
    switch (period) {
      case 'hourly':
        return hourlyData.map(item => ({
          timeLabel: item.hour,
          value: item.count,
          type: item.type
        }));
      case 'daily':
        return dailyData.map(item => ({
          timeLabel: item.day,
          value: item.count,
          type: item.type
        }));
      case 'weekly':
        return weeklyData.map(item => ({
          timeLabel: `Week ${item.week}`,
          value: item.count,
          type: item.type
        }));
      case 'monthly':
        return monthlyData.map(item => ({
          timeLabel: item.month,
          value: item.count,
          type: item.type
        }));
      default:
        return [];
    }
  };

  useEffect(() => {
    if (!containerRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const chartData = getChartData();
    
    if (chartData.length === 0) return;

    const chart = new Line(containerRef.current, {
      data: chartData,
      xField: 'timeLabel',
      yField: 'value',
      seriesField: 'type',
      xAxis: {
        type: 'cat',
        label: {
          formatter: (v) => {
            if (period === 'hourly') return `${v}:00`;
            return v;
          }
        }
      },
      yAxis: {
        label: {
          formatter: (v) => `${v}`
        },
        grid: {
          line: {
            style: {
              lineDash: [4, 4]
            }
          }
        }
      },
      tooltip: {
        formatter: (datum) => ({
          name: datum.type,
          value: datum.value,
          period: datum.timeLabel
        })
      },
      smooth: true,
      animation: {
        appear: {
          animation: 'fade-in',
          duration: 1000
        }
      },
      color: ['#1890ff', '#2fc25b', '#facc14', '#f04864'],
      legend: {
        position: 'top-right',
        marker: {
          symbol: 'circle'
        }
      }
    });

    chartRef.current = chart;
    chart.render();

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [period, hourlyData, dailyData, weeklyData, monthlyData, loading]);

  const getData = () => {
    switch (period) {
      case 'hourly': return hourlyData;
      case 'daily': return dailyData;
      case 'weekly': return weeklyData;
      case 'monthly': return monthlyData;
      default: return [];
    }
  };

  // Show skeleton while loading
  if (loading) {
    return (
      <div style={{ height, padding: '16px' }}>
        <Skeleton active paragraph={{ rows: 6 }} />
      </div>
    );
  }

  if (getData().length === 0) {
    return <NoDataPlaceholder height={height} message={`No ${period} activity data available`} />;
  }

  return (
    <div
      ref={containerRef}
      style={{
        height,
        width: '100%',
        position: 'relative'
      }}
      data-testid="activity-trend-chart"
    />
  );
};